/**
 * Products > Base garments: URL state.
 *
 *   view=base-garments          the Base garments view (absent = Products)
 *   bq=<text>                   search (code or name), sent to the server
 *   bwho=own|all|<client id>    "Who can use it" filter (absent = Anyone)
 *   bpage=<n>                   page, 1-based (absent = 1)
 *   code=<CODE>                 a code to pin first, open and flash (after
 *                               create or copy, or a deep link from Products)
 *   codes=colours|sizes|productTypes
 *                               the Codes panel, open on that tab (BLU-1442;
 *                               absent or anything else = closed)
 *
 * Every value is validated and falls back, so a hand-edited URL never blanks
 * the list. The `b` prefix keeps these apart from Client prices' `p*` params;
 * leaving the view drops all of them (and `code`). Pure: no React.
 */
import {
  BASE_GARMENT_CODE_RE,
  BASE_GARMENT_CODE_TABLE_KINDS,
  BASE_GARMENT_SIZE_COLOUR_CODE_RE,
  type BaseGarmentAudience,
  type BaseGarmentCodeTableKind,
} from '@threadheads/shared';
import { updateSearchParams } from '@/lib/search-params';
import { PRODUCTS_VIEW_PARAM } from '@/features/client-prices/lib/client-prices-url';

export const BASE_GARMENTS_VIEW = 'base-garments';

export const BASE_GARMENTS_PARAM = {
  search: 'bq',
  who: 'bwho',
  page: 'bpage',
  code: 'code',
  codes: 'codes',
} as const;

/** The server caps search at 64 characters. */
export const BASE_GARMENTS_SEARCH_MAX = 64;

/** `bwho` values other than a client id. */
export const WHO_OWN = 'own';
export const WHO_ALL_CLIENTS = 'all';

export type WhoCanUseFilter =
  | { kind: 'any' }
  | { kind: 'own' }
  | { kind: 'all_clients' }
  | { kind: 'client'; clientOrganizationId: string };

export const WHO_ANY: WhoCanUseFilter = { kind: 'any' };

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function parseWhoParam(raw: string | null): WhoCanUseFilter {
  const value = raw?.trim() ?? '';
  if (value === WHO_OWN) return { kind: 'own' };
  if (value === WHO_ALL_CLIENTS) return { kind: 'all_clients' };
  if (UUID_RE.test(value)) return { kind: 'client', clientOrganizationId: value.toLowerCase() };
  return WHO_ANY;
}

/** The `bwho` value, or null for Anyone. */
export function serializeWho(filter: WhoCanUseFilter): string | null {
  switch (filter.kind) {
    case 'own':
      return WHO_OWN;
    case 'all_clients':
      return WHO_ALL_CLIENTS;
    case 'client':
      return filter.clientOrganizationId;
    default:
      return null;
  }
}

/** The list's audience inputs (server-side filter, DESIGN: filter, count and page at one layer). */
export function whoToListInput(filter: WhoCanUseFilter): {
  audience?: BaseGarmentAudience;
  clientOrganizationId?: string;
} {
  switch (filter.kind) {
    case 'own':
      return { audience: 'own' };
    case 'all_clients':
      return { audience: 'all_clients' };
    case 'client':
      return { audience: 'client', clientOrganizationId: filter.clientOrganizationId };
    default:
      return {};
  }
}

export interface BaseGarmentsUrlState {
  search: string;
  who: WhoCanUseFilter;
  page: number;
  /** A valid product code, upper-cased, or null. */
  focusCode: string | null;
}

export function normalizeFocusCode(raw: string | null | undefined): string | null {
  const code = raw?.trim().toUpperCase() ?? '';
  return BASE_GARMENT_CODE_RE.test(code) ? code : null;
}

export function readBaseGarmentsParams(params: URLSearchParams): BaseGarmentsUrlState {
  const pageRaw = Number.parseInt(params.get(BASE_GARMENTS_PARAM.page) ?? '', 10);
  return {
    search: (params.get(BASE_GARMENTS_PARAM.search) ?? '').slice(0, BASE_GARMENTS_SEARCH_MAX),
    who: parseWhoParam(params.get(BASE_GARMENTS_PARAM.who)),
    page: Number.isFinite(pageRaw) && pageRaw >= 1 ? pageRaw : 1,
    focusCode: normalizeFocusCode(params.get(BASE_GARMENTS_PARAM.code)),
  };
}

/** True when a search or a who-can-use filter narrows the list. */
export const isFiltered = (state: Pick<BaseGarmentsUrlState, 'search' | 'who'>): boolean =>
  state.search.trim() !== '' || state.who.kind !== 'any';

/** Open the Base garments view, keeping whatever else the URL holds. */
export function withBaseGarmentsView(prev: URLSearchParams): URLSearchParams {
  return updateSearchParams(prev, { [PRODUCTS_VIEW_PARAM]: BASE_GARMENTS_VIEW });
}

/** Leaving Base garments: drops the view and every Base garments param. */
export function withoutBaseGarmentsParams(prev: URLSearchParams): URLSearchParams {
  return updateSearchParams(prev, {
    [PRODUCTS_VIEW_PARAM]: null,
    ...Object.fromEntries(Object.values(BASE_GARMENTS_PARAM).map((key) => [key, null])),
  });
}

/**
 * Land on one code (after create or copy, R40): pin it first, and clear the
 * search, filter and page so it is visible.
 */
export function focusCodeUpdates(code: string): Record<string, string | null> {
  return {
    [BASE_GARMENTS_PARAM.code]: code,
    [BASE_GARMENTS_PARAM.search]: null,
    [BASE_GARMENTS_PARAM.who]: null,
    [BASE_GARMENTS_PARAM.page]: null,
  };
}

/** The deep link a blank on Products opens (`view=`, because `tab=` is Client prices' sub-tab). */
export function baseGarmentHref(code: string): string {
  const params = new URLSearchParams();
  params.set(PRODUCTS_VIEW_PARAM, BASE_GARMENTS_VIEW);
  params.set(BASE_GARMENTS_PARAM.code, code);
  return `/products?${params.toString()}`;
}

/**
 * The Base garments code a blank SKU lives under (F24): the first segment of a
 * base-garment shaped SKU (code-colour or code-size-colour; the code 1 to 10
 * letters or digits, the size and colour 1 to 6), else null. A blank whose SKU is not that shape is not
 * on the Base garments tab, so it keeps its old editor rather than opening an
 * empty list.
 */
export function baseGarmentCodeOfSku(sku: string | null | undefined): string | null {
  const parts = (sku ?? '').trim().toUpperCase().split('-');
  if (parts.length !== 2 && parts.length !== 3) return null;
  return parts.every((part, i) => (i === 0 ? BASE_GARMENT_CODE_RE : BASE_GARMENT_SIZE_COLOUR_CODE_RE).test(part))
    ? (parts[0] ?? null)
    : null;
}

/**
 * BLU-1436: the product code whose stock type a pick-and-pack SKU takes: the first dash
 * segment of a PAP SKU of any shape, when it is a product code exactly as stored (1 to
 * 10 capital letters or digits, no upper-casing: the SQL matches the SKU text as it is),
 * else null. The browser mirror of the backend's `reservationCodeOf` for PAP. Any other
 * production type is null: its stock type is set in Base garments.
 */
export function papCodeOfSku(sku: string | null | undefined, productionType: string | null | undefined): string | null {
  if (productionType !== 'PAP') return null;
  const code = (sku ?? '').trim().split('-')[0] ?? '';
  return BASE_GARMENT_CODE_RE.test(code) ? code : null;
}

/** The Codes panel's tab from `codes=`, or null (closed) for anything else. */
export function parseCodesParam(raw: string | null): BaseGarmentCodeTableKind | null {
  const value = raw?.trim() ?? '';
  return (BASE_GARMENT_CODE_TABLE_KINDS as readonly string[]).includes(value) ? (value as BaseGarmentCodeTableKind) : null;
}

/** A link that opens Base garments with the Codes panel on one tab (the onboarding step's link). */
export function baseGarmentsCodesHref(kind: BaseGarmentCodeTableKind): string {
  const params = new URLSearchParams();
  params.set(PRODUCTS_VIEW_PARAM, BASE_GARMENTS_VIEW);
  params.set(BASE_GARMENTS_PARAM.codes, kind);
  return `/products?${params.toString()}`;
}
